<template>
    <CredentialsCard
        :title="title"
        :cta-text="ctaText"
        :icon="icon"
        :cta-disabled="disableCTA"
        :processing="registerForm.processing || registerForm.validating"
        @confirm="handleConfirm"
        ><form ref="registerFormRef">
            <div v-if="step === 1" class="flex flex-col gap-2">
                <span class="font-semibold">{{
                    $t("auth.pwa.labels.email")
                }}</span>
                <TextField
                    ref="emailFieldRef"
                    v-model="registerForm.email"
                    name="email"
                    auto-complete="email"
                    input-mode="email"
                    :placeholder="$t('form.email.placeholder')"
                    :error="translateMaybe(registerForm.errors.email)"
                    required
                    :input-class="['h-14']"
                    @blur="onEmailBlur()"
                    @focus="onEmailFocus()"
                    @change="registerForm.validate('email')"
                />
                <PasswordRequirementsTooltip
                    :password="registerForm.password"
                    :email="registerForm.email"
                    :show-tooltip="showEmailRequirementTooltip"
                    :activator="emailFieldRef"
                    position="top"
                    wrapper-class="max-w-screen-90 mx-auto"
                    :show-validator="false"
                    title="Ihre Angaben sind für andere Mitglieder nicht sichtbar."
                    title-class="text-h5 text-gray-800"
                    :icon="markRaw(InfoIcon)"
                    icon-classes="text-lemon-green h-5 w-5"
                    @validated="validPassword = $event"
                />
            </div>
            <div v-if="step === 2" class="flex flex-col gap-2">
                <span class="font-semibold">{{
                    $t("auth.pwa.labels.password")
                }}</span>
                <TextField
                    ref="passwordFieldRef"
                    v-model="registerForm.password"
                    type="password"
                    name="password"
                    :placeholder="$t('form.password.placeholder')"
                    :error="translateMaybe(registerForm.errors.password)"
                    required
                    :input-class="['h-14']"
                    @blur="onPasswordBlur()"
                    @focus="onPasswordFocus()"
                    @change="registerForm.validate('password')"
                />
                <PasswordRequirementsTooltip
                    :password="registerForm.password"
                    :email="registerForm.email"
                    :show-tooltip="showPasswordRequirementTooltip"
                    :activator="passwordFieldRef"
                    position="top"
                    wrapper-class="max-w-screen-90 mx-auto"
                    :show-title="false"
                    @validated="validPassword = $event"
                />
            </div>
            <div
                v-if="step === 3"
                class="flex flex-col justify-center gap-10 pt-2"
                :class="{
                    'pointer-events-none': registerForm.processing,
                }"
            >
                <RadioGroupField
                    v-model="registerForm.gender"
                    :label="$t('auth.register.iam')"
                    name="gender"
                    card-options
                    card-classes="rounded-2xl px-2 shadow-button my-1 h-13"
                    card-option-classes="text-gray-800 text-h2"
                    label-class="text-gray-900 text-h1 font-noto font-bold pb-4"
                    :options="getGenderOptions()"
                    @initial-selection="initialGenderSwap"
                />

                <RadioGroupField
                    v-model="registerForm.gender_search"
                    :label="$t('auth.register.looking_for')"
                    name="gender_search"
                    card-options
                    card-classes="rounded-2xl px-2 shadow-button my-1 h-13"
                    card-option-classes="text-gray-800 text-h2"
                    label-class="text-gray-900 text-h1 font-noto font-bold pb-4"
                    :options="getGenderOptions(true)"
                    @initial-selection="initialGenderSwap"
                />
            </div>
        </form>
    </CredentialsCard>
</template>

<script setup>
import CredentialsCard from "@/Components/Cards/Auth/PWA/CredentialsCard.vue";
import DefaultFemaleIcon from "@/Components/Icons/DefaultFemaleIcon.vue";
import DefaultMaleIcon from "@/Components/Icons/DefaultMaleIcon.vue";
import EmailIcon from "@/Components/Icons/EmailIcon.vue";
import InfoIcon from "@/Components/Icons/InfoIcon.vue";
import LockWithThreeDotsIcon from "@/Components/Icons/LockWithThreeDotsIcon.vue";
import PasswordRequirementsTooltip from "@/Components/Tooltips/PasswordRequirementsTooltip.vue";
import RadioGroupField from "@/Components/FormFields/RadioGroupField.vue";
import TextField from "@/Components/FormFields/TextField.vue";
import { translateMaybe } from "@/Helpers/translateMaybe.js";
import { useForm } from "laravel-precognition-vue-inertia";
import { OPTIONS } from "@/Helpers/constants";
import { computed, markRaw, onMounted, onUnmounted, ref, watch } from "vue";
import { registerWith } from "@/Helpers/socialLogin.js";
import { useI18n } from "vue-i18n";

const props = defineProps({
    driver: {
        type: String,
        default: null,
    },
});

const step = ref(props.driver ? 3 : 1);
const { t } = useI18n({ useScope: "global" });
const registerForm = useForm("post", route("register"), {
    email: "",
    password: "",
    gender: "",
    gender_search: "",
});

const handleConfirm = () => {
    if (step.value === 1) {
        if (registerForm.valid("email")) {
            step.value = 2;
        }

        return;
    }
    if (step.value === 2) {
        if (registerForm.valid("password")) {
            step.value = 3;
        }

        return;
    }

    if (step.value === 3) {
        if (!registerForm.gender || !registerForm.gender_search) {
            return;
        }

        if (props.driver) {
            registerWith(
                props.driver,
                registerForm.gender,
                registerForm.gender_search
            );

            return;
        }

        registerForm.submit();
    }
};

const emailFieldRef = ref(null);
const showEmailRequirementTooltip = ref(false);
const onEmailBlur = () => (showEmailRequirementTooltip.value = false);
const onEmailFocus = () => (showEmailRequirementTooltip.value = true);
const passwordFieldRef = ref(null);
const showPasswordRequirementTooltip = ref(false);
const onPasswordBlur = () => (showPasswordRequirementTooltip.value = false);
const onPasswordFocus = () => (showPasswordRequirementTooltip.value = true);

const icon = computed(() => {
    if (step.value === 1) {
        return markRaw(EmailIcon);
    }

    if (step.value === 2) {
        return markRaw(LockWithThreeDotsIcon);
    }

    return null;
});
const title = computed(() => {
    if (step.value === 3) {
        return null;
    }
    return t("auth.pwa.titles.register_form.step_" + step.value);
});
const ctaText = computed(() => {
    if (step.value === 3) {
        return t("auth.pwa.ctas.start_now");
    }
    return t("auth.pwa.ctas.proceed");
});
const disableCTA = computed(() => {
    if (step.value === 1) {
        return !registerForm.valid("email");
    }
    if (step.value === 2) {
        return !registerForm.valid("password");
    }

    return !registerForm.gender || !registerForm.gender_search;
});

const getGenderOptions = (search = false) => [
    {
        text: search
            ? t("form.gender_search.gender_search_female")
            : t("form.gender.gender_female"),
        value: OPTIONS.GENDERS.FEMALE,
        icon: markRaw(DefaultFemaleIcon),
    },
    {
        text: search
            ? t("form.gender_search.gender_search_male")
            : t("form.gender.gender_male"),
        value: OPTIONS.GENDERS.MALE,
        icon: markRaw(DefaultMaleIcon),
    },
];
const initialGenderSwap = (selectionValues) => {
    if (isGenderSelected.value) return;
    const { selectedValue, name } = selectionValues;
    const oppositeGender = selectedValue === "female" ? "male" : "female";
    if (name === "gender") registerForm.gender_search = oppositeGender;
    if (name === "gender_search") registerForm.gender = oppositeGender;
};
const isGenderSelected = computed(
    () => registerForm.gender || registerForm.gender_search
);

const registerFormRef = ref();

onMounted(() => {
    setTimeout(() => {
        if (registerFormRef.value) {
            registerFormRef.value.addEventListener("keydown", function (event) {
                handleEnterPress(event);
            });
        }
    }, 100);
});

onUnmounted(() => {
    if (registerFormRef.value) {
        registerFormRef.value.removeEventListener("keydown", function (event) {
            handleEnterPress(event);
        });
    }
});

const handleEnterPress = (event) => {
    if (event.key === "Enter") {
        handleConfirm();
        event.preventDefault();
    }
};

watch(
    () => registerForm.email,
    (newValue, oldValue) => {
        const isValidEmail = (email) => {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

            return emailRegex.test(email);
        };

        if (isValidEmail(newValue)) {
            registerForm.validate("email");
        }

        if (isValidEmail(oldValue) && !isValidEmail(newValue)) {
            registerForm.validate("email");
        }
    }
);

watch(
    () => registerForm.password,
    (newValue, oldValue) => {
        const hasEightCharacters = (password) => password.length >= 8;

        if (hasEightCharacters(newValue)) {
            registerForm.validate("password");
        }

        if (hasEightCharacters(oldValue) && !hasEightCharacters(newValue)) {
            registerForm.validate("password");
        }
    }
);
</script>
